/**
 * Подпись под пунктом «Тариф и оплата» в ящике.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧЕЛОВЕК НЕ ОТКРЫВАЕТ ЭКРАН ТАРИФА, ПОКА ВСЁ РАБОТАЕТ.
 *
 * Значит узнать, что срок кончается, он может только оттуда, куда
 * не ходит. Подпись в меню — единственное место, где это попадётся
 * на глаза раньше, чем закроются разделы.
 *
 * ПОКА ВСЁ ХОРОШО — ПОДПИСИ НЕТ. Строка «оплачено до 20 декабря» под
 * каждым открытием ящика не сообщает ничего и приучает не читать
 * это место. Она появляется тогда, когда есть что сказать.
 * ═══════════════════════════════════════════════════════════════════════
 */

/** Через сколько суток до конца начинаем говорить. */
export const SAY_FROM_DAYS = 10;

export function planHint(input: {
  health: 'active' | 'ending' | 'expired';
  daysLeft: number | null;
  frozen: boolean;
} | null): string | null {
  if (input === null) return 'подписки нет';

  // ЗАМОРОЗКА ВАЖНЕЕ СРОКА: она уже случилась, а срок только идёт
  if (input.frozen) return 'заморожена';
  if (input.health === 'expired') return 'кончилась';

  // Бессрочная подписка молчит: сказать о ней нечего
  if (input.daysLeft === null) return null;

  if (input.daysLeft <= 0) return 'кончается сегодня';
  if (input.daysLeft <= SAY_FROM_DAYS) return `осталось ${daysText(input.daysLeft)}`;

  return null;
}

/** Русские окончания: «5 суток», «1 сутки», «2 суток». */
export function daysText(days: number): string {
  const last = days % 10;
  const teen = days % 100 >= 11 && days % 100 <= 14;
  if (!teen && last === 1) return `${days} сутки`;
  return `${days} суток`;
}

/**
 * «5 дней», «1 день», «2 дня» — для капсулы остатка в шапке.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ РЯДОМ С `daysText` ЛЕЖИТ ВТОРОЙ СЧЁТ ТЕХ ЖЕ СУТОК
 *
 * Это не дубль: слова разные, и места у них разные. В подписи под
 * пунктом меню сутки называются сутками («осталось 5 суток») —
 * так говорят о сроке подписки. В капсуле шапки эталон пишет
 * «5 дней»: там место на два слова, и «суток» рядом с числом
 * читается как единица измерения, а не как остаток.
 *
 * Один помощник на оба случая пришлось бы звать с признаком
 * «каким словом», и признак этот пришлось бы объяснять на каждом
 * вызове. Две функции с говорящими именами короче и честнее.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function dayText(days: number): string {
  const last = days % 10;
  const teen = days % 100 >= 11 && days % 100 <= 14;
  if (teen) return `${days} дней`;
  if (last === 1) return `${days} день`;
  if (last >= 2 && last <= 4) return `${days} дня`;
  return `${days} дней`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОДПИСЬ «СЧЕТОВ И ОПЛАТ» В МЕНЮ РАЗДЕЛОВ — «Рабочий · до 14.09.2026».
 *
 * Не то же, что `planHint`. Тот молчит, пока всё хорошо: он предупреждает.
 * А подпись в меню отвечает на вопрос «что у меня вообще за тариф
 * и до какого числа» — и отвечать обязана ВСЕГДА, иначе строка в меню
 * выглядит недогруженной.
 *
 * Порядок ветвей — от беды к покою: заморозка случилась, срок кончился,
 * срок идёт. Бессрочный называется бессрочным, а не оставляется пустым:
 * пустота на месте даты читается как «дату не смогли прочитать».
 * ═══════════════════════════════════════════════════════════════════════
 */
export function подписьТарифа(
  current: {
    planName: string | null;
    health: 'active' | 'ending' | 'expired';
    frozen: boolean;
    expiresAt: string | null;
  } | null,
): string | undefined {
  if (current === null) return 'подписки нет';

  const имя = current.planName ?? 'тариф';
  if (current.frozen) return `${имя} · заморожен`;
  if (current.health === 'expired') return `${имя} · срок кончился`;
  if (current.expiresAt === null) return `${имя} · бессрочно`;

  return `${имя} · до ${датаКоротко(current.expiresAt)}`;
}

/** «2026-09-14T00:00:00.000Z» → «14.09.2026». */
export function датаКоротко(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const дв = (n: number) => String(n).padStart(2, '0');
  return `${дв(d.getUTCDate())}.${дв(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}
